import { Capacitor } from '@capacitor/core';

/**
 * What every "Try again" on a couldn't-load state does: reload the page.
 *
 * Until 2026-10-12 Try again first ran an in-place connection recovery
 * (restart Firestore's network, re-fetch) and only offered "Reload page" after
 * that failed. In practice the in-place retry often did not bring the page
 * back while a reload always did, so the owner asked for Try again to simply
 * be the reload. Where the page has unsaved work the user confirms first,
 * since a reload discards it.
 *
 * @param {object} [p]
 * @param {() => boolean} [p.hasUnsavedWork]
 * @param {() => void} [p.reload]                 test seam
 * @param {(msg: string) => boolean} [p.confirm]  test seam
 * @returns {boolean} whether the reload was started
 */
export const RELOAD_CONFIRM_MESSAGE = 'Reloading will discard what you’ve typed on this page. Reload anyway?';

// One pending "reload when back online" at a time, however often it's tapped.
let waitingForOnline = false;

export function reloadPage({
  hasUnsavedWork,
  reload = () => window.location.reload(),
  confirm = (msg) => window.confirm(msg),
  isNative = Capacitor.isNativePlatform(),
  win = typeof window !== 'undefined' ? window : null,
} = {}) {
  let unsaved = false;
  try { unsaved = Boolean(hasUnsavedWork && hasUnsavedWork()); } catch (e) { unsaved = true; } // when unsure, ask
  if (unsaved && !confirm(RELOAD_CONFIRM_MESSAGE)) return false;
  // On the web there is no service worker, so reloading with no connection
  // swaps the app for the browser's own offline error page. Wait for the
  // connection and reload then. The native app loads from local files, so it
  // can always reload.
  if (!isNative && win && win.navigator && win.navigator.onLine === false) {
    if (!waitingForOnline) {
      waitingForOnline = true;
      win.addEventListener('online', () => { waitingForOnline = false; reload(); }, { once: true });
    }
    return false;
  }
  reload();
  return true;
}
